import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { useProfile, useEntrepreneurProfile, useMentorProfile, useUpdateProfile, useUpdateEntrepreneurProfile, useUpdateMentorProfile, useUserSkills, useUserIndustries } from "@/hooks/useProfile";
import { PitchDeckUpload } from "@/components/profile/PitchDeckUpload";
import { MyBadgesCard } from "@/components/badges/MyBadgesCard";
import { CertificatesSection } from "@/components/profile/CertificatesSection";
import { EmailPreferencesSection } from "@/components/profile/EmailPreferencesSection";
import { useProfilePrivate, useUpdateProfilePrivate } from "@/hooks/useProfilePrivate";
import { MentorTopicsSection } from "@/components/profile/MentorTopicsSection";
import { ProfileViewMode } from "@/components/profile/ProfileViewMode";
import { DualRoleRequestSection } from "@/components/profile/DualRoleRequestSection";
import { SuggestTaxonomyDialog } from "@/components/profile/SuggestTaxonomyDialog";
import { useOnboardingTour } from "@/hooks/useOnboardingTour";
import { PhoneInput, parsePhoneNumber, combinePhoneNumber } from "@/components/ui/phone-input";
import { SuggestedConnectionsModal } from "@/components/SuggestedConnectionsModal";
import { useProfileCompletionTrigger } from "@/hooks/useSuggestedConnections";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock, Clock, RotateCcw, AlertCircle, Pencil, Lightbulb } from "lucide-react";
import { Loader2, Save, X, Upload, Camera, ShieldCheck } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { resizeAndCompressImage, generateAvatarFilename } from "@/lib/imageUtils";
import { getStorageUrl } from "@/lib/storage";
import { AvatarCropDialog } from "@/components/profile/AvatarCropDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { COMPANY_STAGE_OPTIONS, COMPANY_STAGE_HELPER_TEXT } from "@/lib/companyStages";
import { InfoTooltipList } from "@/components/ui/info-tooltip";

const PHONE_TYPES = [
  { value: "mobile", label: "Mobile" },
  { value: "home", label: "Home" },
  { value: "work", label: "Work" },
];

const REFERRED_BY_OPTIONS = [
  { value: "food_for_the_poor", label: "Food For The Poor" },
  { value: "emprezz_golding", label: "Emprezz Golding" },
  { value: "talk_up_yout", label: "Talk Up Yout" },
  { value: "word_of_mouth", label: "Word of Mouth" },
  { value: "social_media", label: "Social Media" },
  { value: "website", label: "Website" },
  { value: "other", label: "Other" },
];

const GENDER_OPTIONS = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer Not to Say" },
];

const AGE_RANGE_OPTIONS = [
  { value: "12-17", label: "12-17" },
  { value: "18-30", label: "18-30" },
  { value: "31-45", label: "31-45" },
  { value: "45+", label: "45+" },
];

const HAS_DEVICE_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "maybe", label: "Maybe" },
];

import { EDUCATION_LEVEL_OPTIONS, EDUCATION_HELPER_TEXT } from "@/lib/educationLevels";

const JAMAICAN_PARISHES = [
  { value: "kingston", label: "Kingston" },
  { value: "st_andrew", label: "St. Andrew" },
  { value: "st_thomas", label: "St. Thomas" },
  { value: "portland", label: "Portland" },
  { value: "st_mary", label: "St. Mary" },
  { value: "st_ann", label: "St. Ann" },
  { value: "trelawny", label: "Trelawny" },
  { value: "st_james", label: "St. James" },
  { value: "hanover", label: "Hanover" },
  { value: "westmoreland", label: "Westmoreland" },
  { value: "st_elizabeth", label: "St. Elizabeth" },
  { value: "manchester", label: "Manchester" },
  { value: "clarendon", label: "Clarendon" },
  { value: "st_catherine", label: "St. Catherine" },
];

const COUNTRY_OPTIONS = [
  { value: "jamaica", label: "Jamaica" },
  { value: "united_states", label: "United States" },
];

export default function Profile() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: profilePrivate, isLoading: privateLoading } = useProfilePrivate();
  const { data: entrepreneurProfile } = useEntrepreneurProfile();
  const { data: mentorProfile } = useMentorProfile();
  const { data: userSkills } = useUserSkills();
  const { data: userIndustries } = useUserIndustries();

  const updateProfile = useUpdateProfile();
  const updateProfilePrivate = useUpdateProfilePrivate();
  const updateEntrepreneurProfile = useUpdateEntrepreneurProfile();
  const updateMentorProfile = useUpdateMentorProfile();
  const queryClient = useQueryClient();
  const { resetTour } = useOnboardingTour();
  const { shouldShowSuggested, triggerAfterProfileCompletion, resetTrigger } = useProfileCompletionTrigger(user?.id);
  const isMobile = useIsMobile();
  const [isScrolled, setIsScrolled] = useState(false);

  // Track scroll position for sticky header shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Form state
  const [fullName, setFullName] = useState("");
  const [bio, setBio] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyDescription, setCompanyDescription] = useState("");
  const [startupStage, setStartupStage] = useState("");
  const [targetMarket, setTargetMarket] = useState("");
  const [fundingStatus, setFundingStatus] = useState("");
  const [specificChallenges, setSpecificChallenges] = useState("");
  const [professionalBackground, setProfessionalBackground] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  const [careerHighlights, setCareerHighlights] = useState("");
  const [availabilityNotes, setAvailabilityNotes] = useState("");

  // New demographic fields
  const [parish, setParish] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+1-JM");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneType, setPhoneType] = useState("");
  const [referredBy, setReferredBy] = useState("");
  const [gender, setGender] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [hasDevice, setHasDevice] = useState("");
  const [educationLevel, setEducationLevel] = useState("");
  const [country, setCountry] = useState("");
  const [isWhatsapp, setIsWhatsapp] = useState(false);

  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showProfileReviewDialog, setShowProfileReviewDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [suggestSkillOpen, setSuggestSkillOpen] = useState(false);
  const [suggestIndustryOpen, setSuggestIndustryOpen] = useState(false);
  const [noLinkedinProfile, setNoLinkedinProfile] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Local state for immediate avatar preview after upload
  const [localAvatarUrl, setLocalAvatarUrl] = useState<string | null>(null);
  // Crop dialog state
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);

  // Load all skills and industries
  const { data: allSkills } = useQuery({
    queryKey: ["skills"],
    queryFn: async () => {
      const { data, error } = await supabase.from("skills").select("*").order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: allIndustries } = useQuery({
    queryKey: ["industries"],
    queryFn: async () => {
      const { data, error } = await supabase.from("industries").select("*").order("name");
      if (error) throw error;
      return data;
    },
  });

  // Initialize form with existing data
  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || "");
      setBio(profile.bio || "");
      setReferredBy(profile.referred_by || "");
      setGender(profile.gender || "");
      setAgeRange(profile.age_range || "");
      setHasDevice(profile.has_device || "");
      setEducationLevel(profile.education_level || "");
    }
  }, [profile]);

  // Initialize form with private data
  useEffect(() => {
    if (profilePrivate) {
      const isNoLinkedin = profilePrivate.linkedin_url === "DOES_NOT_HAVE_LINKEDIN";
      setNoLinkedinProfile(isNoLinkedin);
      setLinkedinUrl(isNoLinkedin ? "" : profilePrivate.linkedin_url || "");
      setParish(profilePrivate.parish || "");
      // Parse phone into country code and number
      const { countryCode, phoneNumber: parsedNumber } = parsePhoneNumber(profilePrivate.phone || "");
      setPhoneCountryCode(countryCode);
      setPhoneNumber(parsedNumber);
      setPhoneType(profilePrivate.phone_type || "");
      setCountry(profilePrivate.country || "");
      setIsWhatsapp(profilePrivate.is_whatsapp || false);
    }
  }, [profilePrivate]);

  useEffect(() => {
    if (entrepreneurProfile) {
      setCompanyName(entrepreneurProfile.company_name || "");
      setCompanyDescription(entrepreneurProfile.company_description || "");
      setStartupStage(entrepreneurProfile.startup_stage || "");
      setTargetMarket(entrepreneurProfile.target_market || "");
      setFundingStatus(entrepreneurProfile.funding_status || "");
      setSpecificChallenges(entrepreneurProfile.specific_challenges || "");
    }
  }, [entrepreneurProfile]);

  useEffect(() => {
    if (mentorProfile) {
      setProfessionalBackground(mentorProfile.professional_background || "");
      setYearsExperience(mentorProfile.years_experience?.toString() || "");
      setCareerHighlights(mentorProfile.career_highlights || "");
      setAvailabilityNotes(mentorProfile.availability_notes || "");
    }
  }, [mentorProfile]);

  useEffect(() => {
    if (userSkills) {
      setSelectedSkills(userSkills.map((s: any) => s.skill_id));
    }
  }, [userSkills]);

  useEffect(() => {
    if (userIndustries) {
      setSelectedIndustries(userIndustries.map((i: any) => i.industry_id));
    }
  }, [userIndustries]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  // Auto-enable edit mode for un-onboarded users
  useEffect(() => {
    if (profile && !profile.is_onboarded) {
      setIsEditMode(true);
    }
  }, [profile]);

  const updateSkills = useMutation({
    mutationFn: async (skillIds: string[]) => {
      if (!user?.id) throw new Error("Not authenticated");

      // Delete existing skills
      await supabase.from("user_skills").delete().eq("user_id", user.id);

      // Insert new skills
      if (skillIds.length > 0) {
        const { error } = await supabase.from("user_skills").insert(
          skillIds.map((skillId) => ({ user_id: user.id, skill_id: skillId }))
        );
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user_skills"] });
    },
  });

  const updateIndustries = useMutation({
    mutationFn: async (industryIds: string[]) => {
      if (!user?.id) throw new Error("Not authenticated");

      await supabase.from("user_industries").delete().eq("user_id", user.id);

      if (industryIds.length > 0) {
        const { error } = await supabase.from("user_industries").insert(
          industryIds.map((industryId) => ({ user_id: user.id, industry_id: industryId }))
        );
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user_industries"] });
    },
  });

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a JPG, PNG, or WebP image");
      return;
    }

    // Validate file size (5MB max before compression)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    // Read as data URL and open crop dialog
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropImageSrc(e.target?.result as string);
      setShowCropDialog(true);
    };
    reader.readAsDataURL(file);

    // Reset file input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    if (!user?.id) return;
    setShowCropDialog(false);
    setCropImageSrc(null);
    setUploadingAvatar(true);
    try {
      // Compress the already-square cropped image to 256x256
      const file = new File([croppedBlob], "cropped.jpg", { type: "image/jpeg" });
      const compressedBlob = await resizeAndCompressImage(file, 256, 256, 0.85);
      
      const filePath = generateAvatarFilename(user.id);

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, compressedBlob, { 
          contentType: 'image/jpeg',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: filePath })
        .eq('id', user.id);

      if (updateError) throw updateError;

      const publicUrl = getStorageUrl("avatars", filePath);
      if (publicUrl) {
        setLocalAvatarUrl(`${publicUrl}?t=${Date.now()}`);
      }

      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Profile photo updated!");
    } catch (error: any) {
      toast.error("Failed to upload photo: " + error.message);
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Validation function for required fields during first-time onboarding
  const validateProfile = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    // Required for all users
    if (!fullName.trim()) errors.fullName = "Full name is required";
    // Check if phone number has at least some digits
    const phoneDigits = phoneNumber.replace(/\D/g, "");
    if (!phoneDigits || phoneDigits.length < 7) errors.phone = "Phone number must have at least 7 digits";
    if (!country) errors.country = "Country is required";
    if (country === "jamaica" && !parish) errors.parish = "Parish is required for Jamaica";
    // LinkedIn is required unless user indicates they don't have one
    if (!linkedinUrl.trim() && !noLinkedinProfile) errors.linkedin = "LinkedIn URL is required";
    
    if (!gender) errors.gender = "Gender is required";
    if (!ageRange) errors.ageRange = "Age range is required";
    if (!referredBy) errors.referredBy = "Referred By is required";
    
    return errors;
  };

  const handleSave = async () => {
    // Clear previous errors
    setFieldErrors({});
    
    try {
      // Only validate required fields for first-time onboarding
      if (!profile?.is_onboarded) {
        const errors = validateProfile();
        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          toast.error("Please complete all required fields");
          
          // Scroll to first error field
          const firstErrorField = Object.keys(errors)[0];
          const element = document.getElementById(firstErrorField);
          element?.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
      }

      // Update public profile - also set is_onboarded to true when profile is saved
      await updateProfile.mutateAsync({
        full_name: fullName,
        bio,
        referred_by: referredBy,
        gender,
        age_range: ageRange,
        has_device: hasDevice,
        education_level: educationLevel,
        is_onboarded: true,
      } as any);

      // Update private profile data
      await updateProfilePrivate.mutateAsync({
        linkedin_url: noLinkedinProfile ? "DOES_NOT_HAVE_LINKEDIN" : linkedinUrl,
        parish,
        phone: combinePhoneNumber(phoneCountryCode, phoneNumber),
        phone_type: phoneType,
        country,
        is_whatsapp: isWhatsapp,
      });

      if (profile?.role === "entrepreneur") {
        await updateEntrepreneurProfile.mutateAsync({
          company_name: companyName || null,
          company_description: companyDescription || null,
          startup_stage: (startupStage as any) || null,
          target_market: targetMarket || null,
          funding_status: fundingStatus || null,
          specific_challenges: specificChallenges || null,
        });
      } else if (profile?.role === "mentor") {
        await updateMentorProfile.mutateAsync({
          professional_background: professionalBackground,
          years_experience: yearsExperience ? parseInt(yearsExperience) : null,
          career_highlights: careerHighlights,
          availability_notes: availabilityNotes,
        });
      }

      await updateSkills.mutateAsync(selectedSkills);
      await updateIndustries.mutateAsync(selectedIndustries);

      toast.success("Profile saved successfully!");
      
      // Exit edit mode after save
      setIsEditMode(false);
      
      // Show profile review dialog if profile is not yet published
      if (!profile?.is_published) {
        setShowProfileReviewDialog(true);
      }
      
      // Trigger suggested connections modal for first-time profile completion
      await triggerAfterProfileCompletion();
    } catch (error) {
      // Individual mutations handle their own errors
    }
  };

  const handleCancel = () => {
    // Clear field errors
    setFieldErrors({});
    
    // Reset form state to original values
    if (profile) {
      setFullName(profile.full_name || "");
      setBio(profile.bio || "");
      setReferredBy(profile.referred_by || "");
      setGender(profile.gender || "");
      setAgeRange(profile.age_range || "");
      setHasDevice(profile.has_device || "");
      setEducationLevel(profile.education_level || "");
    }
    if (profilePrivate) {
      const isNoLinkedin = profilePrivate.linkedin_url === "DOES_NOT_HAVE_LINKEDIN";
      setNoLinkedinProfile(isNoLinkedin);
      setLinkedinUrl(isNoLinkedin ? "" : profilePrivate.linkedin_url || "");
      setParish(profilePrivate.parish || "");
      const { countryCode, phoneNumber: parsedNumber } = parsePhoneNumber(profilePrivate.phone || "");
      setPhoneCountryCode(countryCode);
      setPhoneNumber(parsedNumber);
      setPhoneType(profilePrivate.phone_type || "");
      setCountry(profilePrivate.country || "");
      setIsWhatsapp(profilePrivate.is_whatsapp || false);
    }
    if (entrepreneurProfile) {
      setCompanyName(entrepreneurProfile.company_name || "");
      setCompanyDescription(entrepreneurProfile.company_description || "");
      setStartupStage(entrepreneurProfile.startup_stage || "");
      setTargetMarket(entrepreneurProfile.target_market || "");
      setFundingStatus(entrepreneurProfile.funding_status || "");
      setSpecificChallenges(entrepreneurProfile.specific_challenges || "");
    }
    if (mentorProfile) {
      setProfessionalBackground(mentorProfile.professional_background || "");
      setYearsExperience(mentorProfile.years_experience?.toString() || "");
      setCareerHighlights(mentorProfile.career_highlights || "");
      setAvailabilityNotes(mentorProfile.availability_notes || "");
    }
    if (userSkills) {
      setSelectedSkills(userSkills.map((s: any) => s.skill_id));
    }
    if (userIndustries) {
      setSelectedIndustries(userIndustries.map((i: any) => i.industry_id));
    }
    setIsEditMode(false);
  };
  
  // Helper to clear a field error when user starts typing
  const clearFieldError = (field: string) => {
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const toggleSkill = (skillId: string) => {
    setSelectedSkills((prev) =>
      prev.includes(skillId) ? prev.filter((s) => s !== skillId) : [...prev, skillId]
    );
  };

  const toggleIndustry = (industryId: string) => {
    setSelectedIndustries((prev) =>
      prev.includes(industryId) ? prev.filter((i) => i !== industryId) : [...prev, industryId]
    );
  };

  const isEntrepreneur = profile?.role === "entrepreneur";

  return (
    <>
      <AlertDialog open={showProfileReviewDialog} onOpenChange={setShowProfileReviewDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader className="text-center sm:text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Clock className="h-8 w-8 text-primary" />
            </div>
            <AlertDialogTitle className="text-2xl">Profile Submitted!</AlertDialogTitle>
            <AlertDialogDescription className="text-base space-y-4">
              <p>
                Your profile has been submitted for review. An EAB team member will review your information shortly.
              </p>
              <p>
                You'll receive a notification once your profile has been approved. In the meantime, you can continue to edit your profile.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center">
            <AlertDialogAction className="bg-primary hover:bg-primary/90 min-w-[120px]">
              Got it!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    <DashboardLayout>
      {(authLoading || profileLoading) ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (!user || !profile) ? null : (
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
        {/* Desktop: Sticky header when in edit mode */}
        <div 
          className={`
            ${isEditMode ? 'sticky top-14 sm:top-16 z-20 -mx-4 px-4 py-4 bg-background transition-shadow duration-200' : ''}
            ${isEditMode && isScrolled ? 'shadow-md border-b' : ''}
          `}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">My Profile</h1>
              <p className="text-muted-foreground">
                {isEditMode ? "Edit your profile information" : "View your profile information"}
              </p>
            </div>
            {/* Desktop action buttons - hidden on mobile when editing */}
            <div className={`flex items-center gap-2 ${isEditMode && isMobile ? 'hidden' : ''}`}>
              {isEditMode ? (
                <>
                  {profile.is_onboarded && (
                    <Button 
                      variant="outline" 
                      onClick={handleCancel}
                    >
                      <X className="mr-2 h-4 w-4" />
                      Cancel
                    </Button>
                  )}
                  <Button onClick={handleSave} disabled={updateProfile.isPending}>
                    {updateProfile.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save Changes
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      resetTour.mutate();
                      navigate("/dashboard");
                    }}
                    disabled={resetTour.isPending}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Take Tour Again
                  </Button>
                  <Button onClick={() => setIsEditMode(true)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit Profile
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Welcome Alert for Un-onboarded Users */}
        {!profile.is_onboarded && isEditMode && (
          <Alert className="border-primary bg-primary/5">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Welcome to EAB Connect!</strong> Please complete the required fields 
              (marked with <span className="text-destructive font-medium">*</span>) to finish setting up your profile.
            </AlertDescription>
          </Alert>
        )}

        {/* Dual-role capability request (own profile only, view mode) */}
        {!isEditMode && <DualRoleRequestSection />}

        {/* View Mode */}
        {!isEditMode && (
          <ProfileViewMode
            profile={profile}
            profilePrivate={profilePrivate || null}
            entrepreneurProfile={entrepreneurProfile || null}
            mentorProfile={mentorProfile || null}
            userSkills={userSkills || []}
            userIndustries={userIndustries || []}
          />
        )}

        {/* Edit Mode */}
        {isEditMode && (
          <>

        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Your public profile information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative group">
                <Avatar className="h-20 w-20">
                  <AvatarImage 
                    src={localAvatarUrl || (profile.avatar_url ? getStorageUrl("avatars", profile.avatar_url) || undefined : undefined)} 
                    alt={fullName || "User avatar"}
                  />
                  <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                    {fullName?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  {uploadingAvatar ? (
                    <Loader2 className="h-6 w-6 animate-spin text-white" />
                  ) : (
                    <Camera className="h-6 w-6 text-white" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant={isEntrepreneur ? "default" : "secondary"} className={isEntrepreneur ? "badge-entrepreneur" : "badge-mentor"}>
                    {profile.role}
                  </Badge>
                  {!profile.is_published && (
                    <Badge variant="outline" className="text-warning border-warning">
                      Pending Approval
                    </Badge>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                >
                  {uploadingAvatar ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  Upload Photo
                </Button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name <span className="text-destructive">*</span></Label>
                <Input 
                  id="fullName" 
                  value={fullName} 
                  onChange={(e) => {
                    setFullName(e.target.value);
                    clearFieldError('fullName');
                  }}
                  className={fieldErrors.fullName ? "border-destructive" : ""}
                />
                {fieldErrors.fullName && (
                  <p className="text-sm text-destructive">{fieldErrors.fullName}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" value={profilePrivate?.email || ""} disabled className="bg-muted" />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="country">Country <span className="text-destructive">*</span></Label>
                <Select value={country} onValueChange={(val) => {
                  setCountry(val);
                  clearFieldError('country');
                }}>
                  <SelectTrigger id="country" className={fieldErrors.country ? "border-destructive" : ""}>
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldErrors.country && (
                  <p className="text-sm text-destructive">{fieldErrors.country}</p>
                )}
              </div>
              {country === "jamaica" && (
                <div className="space-y-2">
                  <Label htmlFor="parish">Parish <span className="text-destructive">*</span></Label>
                  <Select value={parish} onValueChange={(val) => {
                    setParish(val);
                    clearFieldError('parish');
                  }}>
                    <SelectTrigger id="parish" className={fieldErrors.parish ? "border-destructive" : ""}>
                      <SelectValue placeholder="Select your parish" />
                    </SelectTrigger>
                    <SelectContent>
                      {JAMAICAN_PARISHES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.parish && (
                    <p className="text-sm text-destructive">{fieldErrors.parish}</p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number <span className="text-destructive">*</span></Label>
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <PhoneInput
                    countryCode={phoneCountryCode}
                    phoneNumber={phoneNumber}
                    onCountryCodeChange={setPhoneCountryCode}
                    onPhoneNumberChange={(val) => {
                      setPhoneNumber(val);
                      clearFieldError('phone');
                    }}
                    inputClassName={fieldErrors.phone ? "border-destructive" : ""}
                  />
                </div>
                <Select value={phoneType} onValueChange={setPhoneType}>
                  <SelectTrigger className="w-28">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-background">
                    {PHONE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {fieldErrors.phone && (
                <p className="text-sm text-destructive">{fieldErrors.phone}</p>
              )}
              <div className="flex items-center space-x-2 pt-1">
                <Checkbox 
                  id="is_whatsapp" 
                  checked={isWhatsapp} 
                  onCheckedChange={(checked) => setIsWhatsapp(checked === true)} 
                />
                <Label htmlFor="is_whatsapp" className="text-sm font-normal cursor-pointer">
                  Is this number associated with WhatsApp?
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="linkedin">
                LinkedIn URL {!noLinkedinProfile && <span className="text-destructive">*</span>}
              </Label>
              {noLinkedinProfile ? (
                <div className="flex items-center h-10 px-3 py-2 rounded-md border border-input bg-muted text-muted-foreground">
                  Does Not Have LinkedIn Profile
                </div>
              ) : (
                <Input 
                  id="linkedin" 
                  placeholder="https://linkedin.com/in/yourprofile" 
                  value={linkedinUrl} 
                  onChange={(e) => {
                    setLinkedinUrl(e.target.value);
                    clearFieldError('linkedin');
                  }}
                  className={fieldErrors.linkedin ? "border-destructive" : ""}
                />
              )}
              {fieldErrors.linkedin && (
                <p className="text-sm text-destructive">{fieldErrors.linkedin}</p>
              )}
              <div className="flex items-center space-x-2 pt-1">
                <Checkbox 
                  id="no_linkedin" 
                  checked={noLinkedinProfile} 
                  onCheckedChange={(checked) => {
                    setNoLinkedinProfile(checked === true);
                    if (checked) {
                      setLinkedinUrl("");
                      clearFieldError('linkedin');
                    }
                  }} 
                />
                <Label htmlFor="no_linkedin" className="text-sm font-normal cursor-pointer">
                  I do not have a LinkedIn profile
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea 
                id="bio" 
                placeholder="Tell us about yourself..." 
                value={bio} 
                onChange={(e) => {
                  setBio(e.target.value);
                  clearFieldError('bio');
                }}
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        {/* Badges Section */}
        <MyBadgesCard userId={user?.id} />

        {/* Certificates Section */}
        <CertificatesSection />

        {/* Private Demographic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Demographic Information
            </CardTitle>
            <CardDescription>
              <Alert className="mt-2 border-primary/20 bg-primary/5">
                <Lock className="h-4 w-4" />
                <AlertDescription>
                  This information will never be made public. It is only used by Entrepreneurs Across Borders for internal record keeping and research purposes.
                </AlertDescription>
              </Alert>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="referredBy">Referred By <span className="text-destructive">*</span></Label>
                <Select value={referredBy} onValueChange={setReferredBy}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select how you heard about us" />
                  </SelectTrigger>
                  <SelectContent>
                    {REFERRED_BY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender">Gender <span className="text-destructive">*</span></Label>
                <Select value={gender} onValueChange={(val) => {
                  setGender(val);
                  clearFieldError('gender');
                }}>
                  <SelectTrigger id="gender" className={fieldErrors.gender ? "border-destructive" : ""}>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDER_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldErrors.gender && (
                  <p className="text-sm text-destructive">{fieldErrors.gender}</p>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ageRange">Age Range <span className="text-destructive">*</span></Label>
                <Select value={ageRange} onValueChange={(val) => {
                  setAgeRange(val);
                  clearFieldError('ageRange');
                }}>
                  <SelectTrigger id="ageRange" className={fieldErrors.ageRange ? "border-destructive" : ""}>
                    <SelectValue placeholder="Select age range" />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_RANGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldErrors.ageRange && (
                  <p className="text-sm text-destructive">{fieldErrors.ageRange}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="hasDevice">Do you have a smartphone, tablet or computer?</Label>
                <Select value={hasDevice} onValueChange={setHasDevice}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {HAS_DEVICE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="educationLevel">Education Level</Label>
              <Select value={educationLevel} onValueChange={setEducationLevel}>
                <SelectTrigger>
                  <SelectValue placeholder="Select education level" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {EDUCATION_LEVEL_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{EDUCATION_HELPER_TEXT}</p>
            </div>
          </CardContent>
        </Card>

        {/* Role-specific Info */}
        {isEntrepreneur ? (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
                <CardDescription>Details about your startup or business</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Company/Startup Name</Label>
                    <Input id="companyName" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="startupStage">Company Stage</Label>
                      <InfoTooltipList
                        title="Company Stage Options"
                        items={COMPANY_STAGE_OPTIONS}
                        ariaLabel="View explanations for all Company Stage options"
                      />
                    </div>
                    <Select value={startupStage} onValueChange={setStartupStage}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select stage" />
                      </SelectTrigger>
                      <SelectContent className="max-h-80">
                        {COMPANY_STAGE_OPTIONS.map((stage) => (
                          <SelectItem 
                            key={stage.value} 
                            value={stage.value}
                            className="py-2"
                          >
                            {stage.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{COMPANY_STAGE_HELPER_TEXT}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companyDescription">Company Description</Label>
                  <Textarea id="companyDescription" placeholder="Describe your company..." value={companyDescription} onChange={(e) => setCompanyDescription(e.target.value)} rows={3} />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="targetMarket">Target Market</Label>
                    <Input id="targetMarket" placeholder="e.g., B2B SaaS, Consumers" value={targetMarket} onChange={(e) => setTargetMarket(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fundingStatus">Funding Status</Label>
                    <Input id="fundingStatus" placeholder="e.g., Bootstrapped, Seed funded" value={fundingStatus} onChange={(e) => setFundingStatus(e.target.value)} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="specificChallenges">Specific Challenges/Needs</Label>
                  <Textarea id="specificChallenges" placeholder="What challenges are you facing?" value={specificChallenges} onChange={(e) => setSpecificChallenges(e.target.value)} rows={3} />
                </div>
              </CardContent>
            </Card>

            {/* Pitch Deck Upload - Entrepreneurs only */}
            <PitchDeckUpload currentPitchDeckUrl={profile.pitch_deck_url} />
          </>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Professional Background</CardTitle>
                <CardDescription>Your expertise and experience</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="professionalBackground">Professional Summary</Label>
                  <Textarea id="professionalBackground" placeholder="Describe your professional background..." value={professionalBackground} onChange={(e) => setProfessionalBackground(e.target.value)} rows={4} />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="yearsExperience">Years of Experience</Label>
                    <Input id="yearsExperience" type="number" min="0" value={yearsExperience} onChange={(e) => setYearsExperience(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="availabilityNotes">Availability</Label>
                    <Input id="availabilityNotes" placeholder="e.g., Evenings, Weekends" value={availabilityNotes} onChange={(e) => setAvailabilityNotes(e.target.value)} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="careerHighlights">Career Highlights</Label>
                  <Textarea id="careerHighlights" placeholder="Key achievements..." value={careerHighlights} onChange={(e) => setCareerHighlights(e.target.value)} rows={3} />
                </div>
              </CardContent>
            </Card>

            {/* Mentor Topics Section - Only for mentors */}
            <MentorTopicsSection />
          </>
        )}

        {/* Industries */}
        <Card>
          <CardHeader>
            <CardTitle>Industries</CardTitle>
            <CardDescription>
              {isEntrepreneur ? "Select your industry/vertical" : "Industries you have experience in"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {allIndustries?.map((industry) => (
                <Badge
                  key={industry.id}
                  variant={selectedIndustries.includes(industry.id) ? "default" : "outline"}
                  className="cursor-pointer transition-colors"
                  onClick={() => toggleIndustry(industry.id)}
                >
                  {industry.name}
                  {selectedIndustries.includes(industry.id) && <X className="ml-1 h-3 w-3" />}
                </Badge>
              ))}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => setSuggestIndustryOpen(true)}
            >
              <Lightbulb className="mr-2 h-4 w-4" />
              Can't find it? Suggest a new industry
            </Button>
          </CardContent>
        </Card>
        <SuggestTaxonomyDialog
          open={suggestIndustryOpen}
          onOpenChange={setSuggestIndustryOpen}
          type="industry"
        />

        {/* Skills */}
        <Card>
          <CardHeader>
            <CardTitle>Areas of Expertise</CardTitle>
            <CardDescription>
              Skills you can mentor on
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {allSkills?.map((skill) => (
                <Badge
                  key={skill.id}
                  variant={selectedSkills.includes(skill.id) ? "default" : "outline"}
                  className="cursor-pointer transition-colors"
                  onClick={() => toggleSkill(skill.id)}
                >
                  {skill.name}
                  {selectedSkills.includes(skill.id) && <X className="ml-1 h-3 w-3" />}
                </Badge>
              ))}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => setSuggestSkillOpen(true)}
            >
              <Lightbulb className="mr-2 h-4 w-4" />
              Can't find it? Suggest a new skill
            </Button>
          </CardContent>
        </Card>
        <SuggestTaxonomyDialog
          open={suggestSkillOpen}
          onOpenChange={setSuggestSkillOpen}
          type="skill"
        />

        {/* Email Preferences */}
        <EmailPreferencesSection />

        {/* Mobile bottom spacer to prevent content from being hidden behind fixed bar */}
        {isEditMode && isMobile && <div className="h-24" />}
        </>
        )}

        {/* Mobile: Fixed bottom action bar when in edit mode */}
        {isEditMode && isMobile && (
          <div className="fixed bottom-0 left-0 right-0 z-40 bg-background border-t shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] p-4">
            <div className="flex items-center gap-3 max-w-4xl mx-auto">
              {profile.is_onboarded && (
                <Button 
                  variant="outline" 
                  onClick={handleCancel}
                  className="flex-1"
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
              )}
              <Button 
                onClick={handleSave} 
                disabled={updateProfile.isPending}
                className={profile.is_onboarded ? "flex-1" : "w-full"}
              >
                {updateProfile.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </div>
      )}
    </DashboardLayout>
    
    <SuggestedConnectionsModal
      isOpen={shouldShowSuggested}
      onClose={resetTrigger}
      currentUserId={user?.id || ""}
      currentUserRole={profile?.role || ""}
    />
    <AvatarCropDialog
      imageSrc={cropImageSrc}
      open={showCropDialog}
      onClose={() => { setShowCropDialog(false); setCropImageSrc(null); }}
      onCropComplete={handleCropComplete}
    />
    </>
  );
}
